import { defineStore } from "pinia";
import { computed, reactive, toRefs, watch } from "vue";
import { useBusinessStore } from "./business";
import type { IDomainCustomerAddress } from "~/domain/customer-address";
import type { IDomainCustomerLoyalty } from "~/domain/customer-loyalty";
import type { SalesChannel } from "~/domain/sales-channels";
import { DEFAULT_CURRENCY } from "~/domain/constants";
import {
  useFeatureFlag,
  useBugReporter,
  useAnalytics,
  useDirectBackend,
  usePinia,
} from "#imports";

// Define the state interface
type IState = {
  isLogged: boolean;
  id: string;
  name: string;
  email: string;
  countryCode: number;
  phoneNumber: number;
  addresses: IDomainCustomerAddress[];
  communicationPreferences: {
    marketing?: boolean;
  };
  loyalty: IDomainCustomerLoyalty;
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLogged: false,
    id: "",
    name: "",
    email: "",
    countryCode: 0,
    phoneNumber: 0,
    addresses: [],
    communicationPreferences: {},
    loyalty: {
      balance: 0,
      lifetimePoints: 0,
      tier: undefined,
      nextTier: undefined,
      wallet: {
        cash: {
          balance: {
            value: 0,
            currency: DEFAULT_CURRENCY,
          },
          expiringAmounts: [],
        },
        points: {
          balance: 0,
          expiringPoints: [],
        },
      },
    },
  };
}

export const useCustomerStore = defineStore("customer", () => {
  const state = reactive<IState>(createInitialState());

  const { reportIdentify, report } = useBugReporter();
  const { identify: featureFlagIdentify } = useFeatureFlag();
  const { identify: analyticsIdentify, anonymize: analyticsanonymize } =
    useAnalytics();

  watch(
    () => state.isLogged,
    () => {
      if (!state.isLogged) {
        reportIdentify(null);
        featureFlagIdentify(undefined);
        analyticsanonymize();
        return;
      }

      reportIdentify({
        id: state.id,
        email: state.email,
      });

      featureFlagIdentify(state.id);

      analyticsIdentify({
        id: state.id,
        email: state.email,
        name: state.name,
      });
    },
  );

  const pinia = usePinia();
  const business = useBusinessStore(pinia);
  const { customerRepo, loyaltyRepo } = useDirectBackend();

  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  async function loadProfile(businessId: string) {
    try {
      const res = await customerRepo.profile();

      if (!res) {
        return new Error("No customer profile found");
      }

      // ensure state is cleared before patching
      reset();

      // patch the incoming data
      patch({
        isLogged: true,
        ...res,
        countryCode:
          res.countryCode !== undefined ? Number(res.countryCode) : 0,
        phoneNumber:
          res.phoneNumber !== undefined ? Number(res.phoneNumber) : 0,
      });

      await Promise.allSettled([
        syncAddresses(businessId),
        loadPrefences(businessId),
      ]);
    } catch (e) {
      report(e);
    }
  }

  async function loadPrefences(businessId: string) {
    try {
      const customerPreference = await customerRepo.getCommunicationPreferences(
        { businessId },
      );

      const hasMarketingPreference = customerPreference.find(
        (item) => item.preferenceType === "OrderUpdatesAndMarketing",
      );

      if (hasMarketingPreference === undefined) return;

      patch({
        communicationPreferences: {
          marketing: hasMarketingPreference.hasOptedIn,
        },
      });
    } catch (e) {
      report(e);
    }
  }

  async function syncLoyalty(
    businessId: string,
    salesChannel: SalesChannel,
    outletId?: string,
  ) {
    if (!state.isLogged) return;

    type CustomerProfile = Awaited<
      ReturnType<
        ReturnType<typeof useDirectBackend>["loyaltyRepo"]["getCustomerProfile"]
      >
    >;

    try {
      const res = await loyaltyRepo.getCustomerProfile({
        businessId,
        salesChannel,
        outletId,
      });

      if (!res) return;

      const parseTier = (tier: CustomerProfile["tier"]) => {
        if (!tier) return undefined;

        return {
          name: tier.name ?? undefined,
          description: tier.description ?? undefined,
          photo: tier.media?.url ?? undefined,
          minPoints: tier.minimumLifetimePoints,
          maxPoints: tier.maximumLifetimePoints,
        };
      };

      patch({
        loyalty: {
          balance: res.balance,
          lifetimePoints: res.lifetimePoints,
          tier: parseTier(res?.tier),
          nextTier: parseTier(res?.nextTier),
          wallet: {
            cash: {
              balance: res.wallet.cash.currentBalance,
              expiringAmounts: res.wallet.cash.expiringAmounts,
            },
            points: {
              balance: res.wallet.points.currentBalance,
              expiringPoints: res.wallet.points.expiringPoints,
            },
          },
        },
      });
    } catch (e) {
      report(e);
    }
  }

  async function trackGoogleConversion(
    _outletId: string,
    _salesChannel: SalesChannel,
  ): Promise<void> {
    // We lost this feature when moved to the new BFF.
    // It should be re-implemented following the same specs
    // but using the archtecture of the new BFF.
    // @TODO: https://github.com/chatfood/web-ordering/pull/1851
    return await Promise.resolve();
  }

  async function syncAddresses(businessId: string) {
    if (!state.isLogged) {
      throw new Error(
        "[CustomerStore] Can't sync addresses of an anonymous customer.",
      );
    }

    try {
      const res = await customerRepo.getAddresses({
        businessId,
        salesChannel: business.salesChannel,
      });

      if (!res) return;

      patch({
        addresses: res,
      });
    } catch (e) {
      report(e);
    }
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    loadProfile,
    syncAddresses,
    syncLoyalty,
    loadPrefences,
    trackGoogleConversion,
  };
});
